import AuthUtils from "../utils/session";
import type { AdminProfile } from "../types/auth.types";

/**
 * The admin the current session belongs to.
 *
 * Read by the rail's foot, the dashboard's greeting and Settings, all of which
 * render behind `RequireAuth` — so there is always a session by the time this
 * runs.
 *
 * ## Why there is no request here
 *
 * There used to be: `GET /admin/get_admin_details/:id`, cached for the life of
 * the tab so the rail did not re-fetch on every navigation. That endpoint does
 * not exist. It never did — `/admin/` on the API host is Django's own admin
 * site, which answers a GET with a 302 to its login page, so the call could
 * only ever fail. The cache, the loading state and the failure state were all
 * machinery around a request that had nothing to ask.
 *
 * What replaced it was already in hand. Verifying the one-time code answers
 * with the admin — id, name, address — and the session keeps it, so who is
 * signed in is known the moment they are signed in, with no second call, no
 * cache to invalidate and no window where the rail shows an address while a
 * name is on its way. Signing out clears the session and takes the name with
 * it.
 *
 * The day the backoffice API grows a real profile route — a role, an avatar,
 * the date the account was made — it belongs here, as a fetch keyed on the
 * session's `admin_id`, and this becomes a hook again.
 */
export function useAdmin() {
  const session = AuthUtils.getSession();

  const admin: AdminProfile | null = session
    ? {
        id: session.admin_id,
        // A session stored before the name was kept has none — see
        // `StoredSession.name`. Every reader already falls back to the address.
        name: session.name ?? "",
        email: session.email,
      }
    : null;

  return {
    admin,
    /** The address on the session, which is known without any fetch at all. */
    email: session?.email ?? "",
  };
}

export default useAdmin;
